import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, TrendingUp, Star, Zap, Search, Trophy } from "lucide-react";
import Header from "../components/Header";
import PageHero from "../components/PageHero";
import FeatureCardGrid from "../components/FeatureCardGrid";
import Footer from "../components/Footer";
import { useI18n } from "../lib/i18n";
import { CITY_AGENTS, CITIES, initials } from "../lib/agents";

// ─── Legacy inline data kept for CITY_AGENTS import fallback ──────────────────
// (Data moved to src/lib/agents.js)
const _CITY_AGENTS_UNUSED = {
  Mumbai: [
    { rank: 1, name: "Imran Khan",    area: "Andheri West", rating: 4.9, deals: 47 },
    { rank: 2, name: "Priya Sharma",  area: "Bandra",       rating: 4.8, deals: 39 },
    { rank: 3, name: "Ravi Nair",     area: "Powai",        rating: 4.8, deals: 35 },
    { rank: 4, name: "Sunita Joshi",  area: "Thane",        rating: 4.7, deals: 31 },
    { rank: 5, name: "Arjun Mehta",   area: "Borivali",     rating: 4.7, deals: 28 },
  ],
  Bangalore: [
    { rank: 1, name: "Deepa Krishnan", area: "Koramangala",  rating: 4.9, deals: 41 },
    { rank: 2, name: "Rahul Shetty",   area: "Indiranagar",  rating: 4.8, deals: 36 },
    { rank: 3, name: "Meera Iyer",     area: "Whitefield",   rating: 4.7, deals: 29 },
    { rank: 4, name: "Karthik Reddy",  area: "HSR Layout",   rating: 4.7, deals: 27 },
    { rank: 5, name: "Anita Nair",     area: "Marathahalli", rating: 4.6, deals: 24 },
  ],
  Pune: [
    { rank: 1, name: "Vikram Desai",      area: "Kothrud",     rating: 4.9, deals: 33 },
    { rank: 2, name: "Sneha Kulkarni",    area: "Wakad",       rating: 4.8, deals: 28 },
    { rank: 3, name: "Amit Joshi",        area: "Hinjewadi",   rating: 4.7, deals: 25 },
    { rank: 4, name: "Pooja Patil",       area: "Baner",       rating: 4.7, deals: 22 },
    { rank: 5, name: "Rohit Deshpande",   area: "Viman Nagar", rating: 4.6, deals: 19 },
  ],
  Delhi: [
    { rank: 1, name: "Manish Gupta",  area: "Dwarka",       rating: 4.9, deals: 38 },
    { rank: 2, name: "Neha Singh",    area: "Lajpat Nagar", rating: 4.8, deals: 31 },
    { rank: 3, name: "Rajesh Kumar",  area: "Rohini",       rating: 4.7, deals: 26 },
    { rank: 4, name: "Anjali Sharma", area: "Saket",        rating: 4.7, deals: 23 },
    { rank: 5, name: "Vikash Yadav",  area: "Noida",        rating: 4.6, deals: 21 },
  ],
  Hyderabad: [
    { rank: 1, name: "Srinivas Rao",  area: "Jubilee Hills", rating: 4.9, deals: 35 },
    { rank: 2, name: "Lakshmi Devi",  area: "Banjara Hills", rating: 4.8, deals: 29 },
    { rank: 3, name: "Arun Kumar",    area: "Madhapur",      rating: 4.7, deals: 24 },
    { rank: 4, name: "Preethi Reddy", area: "Gachibowli",    rating: 4.7, deals: 21 },
    { rank: 5, name: "Suresh Babu",   area: "Kondapur",      rating: 4.6, deals: 18 },
  ],
  Chennai: [
    { rank: 1, name: "Karthik Suresh",  area: "Adyar",          rating: 4.9, deals: 31 },
    { rank: 2, name: "Divya Menon",     area: "Anna Nagar",      rating: 4.8, deals: 26 },
    { rank: 3, name: "Ramesh Babu",     area: "Velachery",       rating: 4.7, deals: 22 },
    { rank: 4, name: "Shalini Kumar",   area: "Nungambakkam",    rating: 4.7, deals: 19 },
    { rank: 5, name: "Vijay Anand",     area: "T Nagar",         rating: 4.6, deals: 17 },
  ],
};

function StarRating({ value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
      <Star size={12} fill="#F97316" stroke="none" />
      <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#F97316" }}>{value}</span>
    </div>
  );
}

function AgentCard({ agent }) {
  return (
    <div style={{
      background: "#111111",
      border: "1px solid rgba(249,115,22,0.15)",
      borderRadius: 12,
      padding: "18px 16px",
      display: "flex",
      alignItems: "center",
      gap: 14,
    }}>
      {/* Rank */}
      <div style={{
        minWidth: 28, fontSize: "1rem", fontWeight: 800, color: "#F97316", textAlign: "center",
      }}>
        #{agent.rank}
      </div>
      {/* Avatar */}
      <div style={{
        width: 40, height: 40, borderRadius: "50%",
        background: "linear-gradient(135deg, #F97316, #8B5CF6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 700, fontSize: "0.8rem", flexShrink: 0,
      }}>
        {initials(agent.name)}
      </div>
      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: "0.88rem", fontWeight: 600, color: "#F5F5F5", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {agent.name}
        </p>
        <p style={{ fontSize: "0.75rem", color: "#9CA3AF", margin: "2px 0 4px" }}>{agent.area}</p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.2)", borderRadius: 4, padding: "1px 6px" }}>
          <span style={{ fontSize: "0.65rem", fontWeight: 600, color: "#F97316" }}>✓ Orello Trusted Partner</span>
        </div>
      </div>
      {/* Stats */}
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <StarRating value={agent.rating} />
        <p style={{ fontSize: "0.72rem", color: "#6B7280", margin: "4px 0 0" }}>{agent.deals} deals</p>
      </div>
    </div>
  );
}

// ─── Platform UI Mockup ───────────────────────────────────────────────────────

function PlatformMockup() {
  return (
    <div style={{
      background: "#0D0D0D",
      border: "1px solid rgba(245,245,245,0.08)",
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(249,115,22,0.08)",
    }}>
      {/* Window chrome */}
      <div style={{ background: "#1A1A1A", padding: "10px 16px", display: "flex", alignItems: "center", gap: 6, borderBottom: "1px solid rgba(245,245,245,0.06)" }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#EF4444" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#F59E0B" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#10B981" }} />
        <div style={{ marginLeft: 12, fontSize: "0.7rem", color: "#4B5563" }}>orello.in/app</div>
      </div>

      <div style={{ display: "flex", minHeight: 340 }}>
        {/* Sidebar */}
        <div style={{ width: 160, background: "#111111", borderRight: "1px solid rgba(245,245,245,0.06)", padding: "16px 0", flexShrink: 0 }}>
          <div style={{ padding: "0 12px 16px", borderBottom: "1px solid rgba(245,245,245,0.06)", marginBottom: 8 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#F97316" }}>Orello</div>
            <div style={{ fontSize: "0.62rem", color: "#6B7280", marginTop: 2 }}>Agent Dashboard</div>
          </div>
          {[
            { label: "Dashboard", active: false },
            { label: "Agreements", active: true },
            { label: "Deposits", active: false },
            { label: "Reports", active: false },
            { label: "Earnings", active: false },
          ].map((item) => (
            <div key={item.label} style={{
              padding: "7px 14px",
              fontSize: "0.72rem",
              color: item.active ? "#F97316" : "#6B7280",
              fontWeight: item.active ? 600 : 400,
              background: item.active ? "rgba(249,115,22,0.08)" : "transparent",
              borderLeft: item.active ? "2px solid #F97316" : "2px solid transparent",
              cursor: "pointer",
            }}>
              {item.label}
            </div>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, padding: "16px", display: "flex", flexDirection: "column", gap: 10, background: "#0D0D0D" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#F5F5F5", marginBottom: 4 }}>Active Agreements</div>

          {/* Card 1 */}
          <div style={{
            background: "#111111",
            border: "1px solid rgba(249,115,22,0.15)",
            borderRadius: 10,
            padding: "12px 14px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <p style={{ fontSize: "0.78rem", fontWeight: 600, color: "#F5F5F5", margin: 0 }}>Rahul Verma</p>
                <p style={{ fontSize: "0.65rem", color: "#9CA3AF", margin: "2px 0 0" }}>2BHK · Indiranagar, Bangalore</p>
              </div>
              <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#10B981", background: "rgba(16,185,129,0.1)", padding: "2px 8px", borderRadius: 4 }}>
                Deposit Secured ✓
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["Signed", "Deposit Paid", "Move-in Done"].map((tag) => (
                <span key={tag} style={{
                  fontSize: "0.6rem", padding: "2px 7px", borderRadius: 4,
                  background: "rgba(249,115,22,0.1)", color: "#F97316", border: "1px solid rgba(249,115,22,0.2)",
                }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Card 2 */}
          <div style={{
            background: "#111111",
            border: "1px solid rgba(16,185,129,0.2)",
            borderRadius: 10,
            padding: "12px 14px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <p style={{ fontSize: "0.78rem", fontWeight: 600, color: "#F5F5F5", margin: 0 }}>Meena Pillai</p>
                <p style={{ fontSize: "0.65rem", color: "#9CA3AF", margin: "2px 0 0" }}>1BHK · Koramangala, Bangalore</p>
              </div>
              <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#10B981" }}>₹45,000 returned</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <span style={{
                fontSize: "0.6rem", padding: "2px 7px", borderRadius: 4,
                background: "rgba(16,185,129,0.1)", color: "#10B981", border: "1px solid rgba(16,185,129,0.2)",
              }}>
                Settlement Complete ✓
              </span>
            </div>
          </div>

          {/* Card 3 placeholder */}
          <div style={{
            background: "#111111",
            border: "1px solid rgba(245,245,245,0.06)",
            borderRadius: 10,
            padding: "12px 14px",
            opacity: 0.4,
          }}>
            <div style={{ height: 10, width: "60%", background: "rgba(245,245,245,0.1)", borderRadius: 4, marginBottom: 6 }} />
            <div style={{ height: 8, width: "40%", background: "rgba(245,245,245,0.06)", borderRadius: 4 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ForBrokers() {
  const { t } = useI18n();
  const [city, setCity]           = useState("Bangalore");
  const [citySearch, setCitySearch] = useState("Bangalore");
  const [showCities, setShowCities] = useState(false);

  const features = [
    {
      icon: "FileSignature",
      title: "Agreement in Minutes",
      description: "Create a legally valid, e-stamped rental agreement in one flow. KYC, deposit, condition report, digital signatures — all done before the client leaves your office.",
    },
    {
      icon: "BarChart3",
      title: "Everything in One Dashboard",
      description: "All your active agreements, check-in reports, deposits, dispute status, and deal history — on any device, any time.",
    },
    {
      icon: "TrendingUp",
      title: "Stop Being the Middleman",
      description: "Disputes go to ORE-AI, not your phone. Tenants and landlords resolve it on the platform — you stay completely out of it.",
    },
    {
      icon: "Zap",
      title: "Faster Closures",
      description: "From KYC verification to digital signature in minutes. No printing, no physical stamps, no running to the sub-registrar.",
    },
  ];

  return (
    <div style={{ background: "#080808", color: "#F5F5F5", minHeight: "100vh" }}>
      <Header />

      {/* Hero */}
      <PageHero
        eyebrow={t("nav_for_brokers")}
        title={t("close_more_deals")}
        subtitle="You spend half your time doing paperwork and the other half mediating deposit fights. Orello handles both — so you can focus on closing deals."
        cta1Text="Join as an Agent"
        cta1Link="/register"
        hasGlow={true}
        gradientWord="less"
      />

      {/* Features */}
      <section style={{ background: "#0B0B14", padding: "4rem 0" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2
            className="font-display font-bold mb-16 text-center"
            style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "#F5F5F5" }}
          >
            Tools Built for Your Business
          </h2>
          <FeatureCardGrid features={features} />
        </div>
      </section>

      {/* Earnings Model — new, no commission % */}
      <section style={{ background: "#111111", padding: "5rem 0" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
                <p className="text-xs tracking-widest uppercase" style={{
                  background: "linear-gradient(135deg, #F97316, #A78BFA)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                }}>
                  Earnings Model
                </p>
              </div>
              <h3 className="font-display font-bold mb-5" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", color: "#F5F5F5", lineHeight: 1.2 }}>
                Charge your brokerage transparently. Earn referral bonuses on top.
              </h3>
              <p className="text-base font-light leading-relaxed mb-10" style={{ color: "#D1D5DB", maxWidth: 480 }}>
                Set your brokerage fee — from the tenant, landlord, or split — and it's collected transparently through the platform. No awkward conversations, no chasing payments. Refer other agents and earn bonuses on every deal they close.
              </p>
              <Link
                to="/register"
                className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full text-white"
                style={{
                  background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
                  fontSize: "1rem",
                  boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
                }}
              >
                Join Free & Start Earning <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* How earnings work */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { icon: "📋", title: "Create the agreement", body: "Add tenant, landlord, and set your brokerage fee. All parties get a link to sign digitally and pay." },
                { icon: "💰", title: "Brokerage auto-collected", body: "Your fee is split from the deposit payment flow and credited to your Orello Wallet — no follow-ups needed." },
                { icon: "🤝", title: "Refer agents & earn bonuses", body: "Invite other brokers to Orello. Earn referral bonuses for every deal they close on the platform." },
              ].map((step) => (
                <div key={step.title} style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(249,115,22,0.12)",
                  borderLeft: "3px solid #F97316",
                  borderRadius: 10,
                  padding: "16px 18px",
                  display: "flex",
                  gap: 14,
                  alignItems: "flex-start",
                }}>
                  <span style={{ fontSize: "1.4rem", flexShrink: 0 }}>{step.icon}</span>
                  <div>
                    <p style={{ fontSize: "0.88rem", fontWeight: 600, color: "#F5F5F5", margin: 0 }}>{step.title}</p>
                    <p style={{ fontSize: "0.8rem", color: "#D1D5DB", margin: "4px 0 0", lineHeight: 1.5 }}>{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platform UI Mockup */}
      <section style={{ background: "#080808", padding: "5rem 0" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
                <p className="text-xs tracking-widest uppercase" style={{
                  background: "linear-gradient(135deg, #F97316, #A78BFA)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                }}>
                  The Platform
                </p>
              </div>
              <h3 className="font-display font-bold mb-5" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", color: "#F5F5F5" }}>
                Your entire rental lifecycle in one dashboard.
              </h3>
              <p className="text-base font-light leading-relaxed mb-6" style={{ color: "#D1D5DB", maxWidth: 440 }}>
                Agreements, KYC, deposit collection, check-in reports with photos, checkout, deduction review, ORE-AI dispute resolution, settlement — every step managed on Orello. Free to join and start.
              </p>
              <Link
                to="/register"
                className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full text-white"
                style={{
                  background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
                  fontSize: "1rem",
                  boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
                }}
              >
                Create Free Account <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <PlatformMockup />
          </div>
        </div>
      </section>

      {/* Trusted Partners Leaderboard — broker competitive ranking table */}
      <section style={{ background: "#111111", padding: "5rem 0" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
            <p className="text-xs tracking-widest uppercase" style={{
              background: "linear-gradient(135deg, #F97316, #A78BFA)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            }}>Orello Trusted Partners</p>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="font-display font-bold" style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", color: "#F5F5F5", lineHeight: 1.2 }}>
                Where do you rank<br />in your city?
              </h2>
              <p className="mt-2" style={{ fontSize: "0.88rem", color: "#9CA3AF" }}>
                Ranked by deals closed & tenant ratings on Orello.
              </p>
            </div>
          </div>

          {/* Search bar */}
          <div className="relative mb-6">
            <Search size={16} style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#6B7280" }} />
            <input
              type="text"
              placeholder="Search city — Mumbai, Bangalore, Pune…"
              value={citySearch}
              onChange={e => { setCitySearch(e.target.value); }}
              onFocus={() => setShowCities(true)}
              onBlur={() => setTimeout(() => setShowCities(false), 150)}
              style={{
                width: "100%", padding: "13px 16px 13px 44px",
                background: "#1A1A1A", border: "1px solid rgba(249,115,22,0.25)",
                borderRadius: 12, color: "#F5F5F5", fontSize: "0.9rem", outline: "none",
              }}
            />
            {showCities && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
                background: "#1A1A1A", border: "1px solid rgba(249,115,22,0.2)",
                borderRadius: 10, zIndex: 10, overflow: "hidden",
              }}>
                {CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase())).map(c => (
                  <button key={c} onMouseDown={() => { setCity(c); setCitySearch(c); setShowCities(false); }}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "10px 16px", fontSize: "0.88rem",
                      color: c === city ? "#F97316" : "#D1D5DB",
                      background: c === city ? "rgba(249,115,22,0.08)" : "transparent",
                      border: "none", cursor: "pointer",
                    }}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Ranking table */}
          <div style={{ border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, overflow: "hidden" }}>
            {/* Table header */}
            <div style={{
              display: "grid", gridTemplateColumns: "44px 1fr 80px 80px 72px",
              padding: "10px 20px", background: "rgba(255,255,255,0.03)",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}>
              {["#", "Agent", "Deals", "Rating", ""].map((h, i) => (
                <div key={i} style={{ fontSize: "0.7rem", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.1em", textAlign: i > 1 ? "center" : "left" }}>{h}</div>
              ))}
            </div>

            {/* Rows */}
            {CITY_AGENTS[city].map((agent, i) => {
              const rankColors = ["#F97316", "#FB923C", "#A78BFA", "#9CA3AF", "#9CA3AF"];
              const isFirst = i === 0;
              return (
                <div key={agent.rank} style={{
                  display: "grid", gridTemplateColumns: "44px 1fr 80px 80px 72px",
                  padding: "14px 20px", alignItems: "center",
                  background: isFirst ? "rgba(249,115,22,0.04)" : "transparent",
                  borderBottom: i < CITY_AGENTS[city].length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                  transition: "background 0.15s",
                }}>
                  {/* Rank */}
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {isFirst ? <Trophy size={16} style={{ color: "#F97316" }} /> :
                      <span style={{ fontSize: "0.88rem", fontWeight: 700, color: rankColors[i] }}>#{agent.rank}</span>}
                  </div>
                  {/* Name + Area */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: isFirst ? "linear-gradient(135deg, #F97316, #8B5CF6)" : "rgba(255,255,255,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontWeight: 700, fontSize: "0.72rem", flexShrink: 0,
                    }}>{initials(agent.name)}</div>
                    <div>
                      <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#F5F5F5" }}>{agent.name}</div>
                      <div style={{ fontSize: "0.72rem", color: "#6B7280", marginTop: 1 }}>{agent.area}</div>
                    </div>
                  </div>
                  {/* Deals */}
                  <div style={{ textAlign: "center", fontSize: "0.88rem", fontWeight: 700, color: isFirst ? "#F97316" : "#D1D5DB" }}>{agent.deals}</div>
                  {/* Rating */}
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#F97316" }}>★ {agent.rating}</span>
                  </div>
                  {/* Badge */}
                  <div style={{ textAlign: "center" }}>
                    <span style={{
                      fontSize: "0.58rem", fontWeight: 600, padding: "3px 7px", borderRadius: 4,
                      background: "rgba(249,115,22,0.1)", color: "#F97316", border: "1px solid rgba(249,115,22,0.2)",
                    }}>✓ Verified</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <Link to="/register"
              className="btn-press inline-flex items-center gap-2 font-medium px-7 py-3.5 rounded-full text-white"
              style={{ background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)", fontSize: "0.95rem", boxShadow: "0 8px 32px rgba(139,92,246,0.25)" }}>
              Join & Climb the Rankings <ArrowRight className="h-4 w-4" />
            </Link>
            <p style={{ fontSize: "0.78rem", color: "#6B7280", marginTop: 12 }}>Free to join. Rankings update after every completed deal.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "#080808", padding: "4rem 0" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="font-display font-bold leading-tight mb-6"
            style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: "#F5F5F5" }}
          >
            Scale your brokerage.<br />
            <span style={{
              background: "linear-gradient(135deg, #F97316, #8B5CF6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              smartly.
            </span>
          </h2>
          <p className="text-base font-light mb-12" style={{ color: "#D1D5DB" }}>
            Create unlimited agreements. Earn on every deal you close.
          </p>
          <Link
            to="/register"
            className="btn-press inline-flex items-center gap-2 font-medium px-8 py-4 rounded-full text-white"
            style={{
              background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
              fontSize: "1.05rem",
              boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
            }}
          >
            Create Free Account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
